/**
 * TerraMatrix Data Service - Schema API Service
 *
 * Admin API for managing Schemas (data structure definitions with multiple fields).
 * Connects to the data-service backend (port 8002).
 *
 * Schema = Container with multiple fields (like a database table)
 */

import { config as appConfig } from '@/config';
import { createDataServiceClient } from './data-service-client';
import type { DataServiceListingScope } from './data-service-client';
import type { AxiosRequestConfig } from 'axios';

const dataServiceClient = createDataServiceClient({ baseURL: appConfig.api.dataServiceUrl });

function getScopedRequestConfig(
  organizationId?: string | null,
): AxiosRequestConfig | undefined {
  if (typeof organizationId !== 'string' || organizationId.trim().length === 0) {
    return undefined;
  }

  return {
    headers: {
      'X-Organization-Id': organizationId.trim(),
    },
  };
}

// ============================================================================
// Types - Field Level
// ============================================================================

export type SchemaFieldType = 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'array' | 'object';
export type SchemaStatus = 'draft' | 'published' | 'deprecated';
export type SchemaVisibility = 'private' | 'shared' | 'public';

// Storage convention types — declare how a schema's date/numeric values are
// stored on disk. Read by the materializer to format $match comparisons
// correctly per-schema. Mirrors data-service/backend/src/types/index.ts.
// See Documents/Roadmaps/Schema_Driven_Query_Materialization_Roadmap.md.
export type StorageTimezone =
  | 'utc'
  | 'america-los-angeles'
  | 'america-new-york'
  | 'america-chicago'
  | 'america-denver'
  | 'naive-local';

export type DatetimeStorageFormat =
  | 'iso-8601'
  | 'bson-date'
  | 'unix-epoch-ms'
  | 'unix-epoch-s';

export interface DatetimeStorage {
  timezone: StorageTimezone;
  format: DatetimeStorageFormat;
  hasTimezoneMarker: boolean;
}

export interface NumericStorage {
  unit?: string;
  scale?: number;
}

export interface SamplingCadence {
  typicalIntervalSeconds?: number;
  freshnessAlertSeconds?: number;
}

export interface StorageMetadata {
  datetime?: DatetimeStorage;
  numeric?: NumericStorage;
  samplingCadence?: SamplingCadence;
}

// UI option helpers — used by the admin schema dialog
export const STORAGE_TIMEZONE_OPTIONS: ReadonlyArray<{ value: StorageTimezone; label: string }> = [
  { value: 'utc', label: 'UTC' },
  { value: 'america-los-angeles', label: 'America/Los_Angeles (Pacific)' },
  { value: 'america-new-york', label: 'America/New_York (Eastern)' },
  { value: 'america-chicago', label: 'America/Chicago (Central)' },
  { value: 'america-denver', label: 'America/Denver (Mountain)' },
  { value: 'naive-local', label: 'Naive local (no timezone marker)' },
];

export const DATETIME_STORAGE_FORMAT_OPTIONS: ReadonlyArray<{ value: DatetimeStorageFormat; label: string }> = [
  { value: 'iso-8601', label: 'ISO 8601 string (e.g. 2026-05-27T01:35:42.944Z)' },
  { value: 'bson-date', label: 'BSON Date (native MongoDB date)' },
  { value: 'unix-epoch-ms', label: 'Unix epoch milliseconds (number)' },
  { value: 'unix-epoch-s', label: 'Unix epoch seconds (number)' },
];
export type StringFormat = 'email' | 'url' | 'uuid' | 'datetime';

function normalizeSchemaFieldType(value: string | undefined): SchemaFieldType {
  switch (value?.toLowerCase()) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime';
    case 'array':
      return 'array';
    case 'object':
      return 'object';
    case 'string':
    default:
      return 'string';
  }
}

function normalizeSchemaVisibility(value: string | undefined): SchemaVisibility {
  switch (value?.toLowerCase()) {
    case 'shared':
      return 'shared';
    case 'public':
      return 'public';
    case 'private':
    default:
      return 'private';
  }
}

function normalizeSchemaStatus(value: string | undefined): SchemaStatus {
  switch (value?.toLowerCase()) {
    case 'published':
      return 'published';
    case 'deprecated':
      return 'deprecated';
    case 'draft':
    default:
      return 'draft';
  }
}

// Field-level constraints
export interface SchemaFieldConstraints {
  // Number constraints
  min?: number;
  max?: number;
  precision?: number;

  // String constraints
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: StringFormat;
  enum?: string[];

  // Date constraints
  minDate?: string;
  maxDate?: string;
  dateFormat?: string;

  // Array constraints
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
  itemType?: SchemaFieldType;
}

// Individual field within a schema
export interface SchemaField {
  id: string;
  name: string;
  type: SchemaFieldType;
  required: boolean;
  description?: string;
  constraints?: SchemaFieldConstraints;
  /**
   * Storage convention for date/datetime fields. Required reading by the
   * materializer to format $match comparisons correctly. Populated only
   * when type === 'date' or type === 'datetime'; ignored for other types.
   * See Documents/Roadmaps/Schema_Driven_Query_Materialization_Roadmap.md.
   */
  storage?: DatetimeStorage;
}

// ============================================================================
// Types - Schema Level (Collection of Fields)
// ============================================================================

// Metadata for human-readable information
export interface SchemaMetadata {
  description: string;
  unitOfMeasure?: string;
  usageContext?: string;
  meaning?: string;
  examples?: unknown[];
  tags?: string[];
}

// Full Schema type (container with multiple fields)
export interface Schema {
  id: string;
  organizationId: string;
  groupId?: string;
  name: string;
  displayName: string;
  description: string;
  fields: SchemaField[];
  currentVersion: number;
  latestPublishedVersion: number;
  visibility: SchemaVisibility;
  status: SchemaStatus;
  deprecatedAt?: string;
  deprecatedReason?: string;
  replacedBy?: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// Create Schema Input (frontend format). Per-field storage convention
// lives on each SchemaField; no schema-level storage block.
export interface CreateSchemaInput {
  name: string;
  displayName: string;
  description: string;
  fields: SchemaField[];
  visibility?: SchemaVisibility;
  tags?: string[];
}

export interface DataServiceScopeOptions {
  organizationId?: string | null;
}

// Update Schema Input
export interface UpdateSchemaInput {
  displayName?: string;
  description?: string;
  fields?: SchemaField[];
  visibility?: SchemaVisibility;
  tags?: string[];
}

// List options
export interface ListSchemasOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: SchemaStatus;
  visibility?: SchemaVisibility;
  scope?: DataServiceListingScope;
}

// Pagination
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface PaginatedResult<T> {
  data: T[];
  pagination: PaginationInfo;
}

export interface SchemaGroupMembershipSummary {
  groupId: string;
  groupName: string;
  groupSlug: string;
  collectionName: string;
  status: string;
}

// ============================================================================
// Backend Types (for transformation)
// ============================================================================

interface BackendSchemaField {
  id?: string;
  name: string;
  type: string; // Backend uses uppercase: 'STRING', 'NUMBER', etc.
  required?: boolean;
  description?: string;
  constraints?: SchemaFieldConstraints;
  storage?: DatetimeStorage;
}

interface BackendSchema {
  _id?: string;
  id?: string;
  organizationId?: string;
  org_id?: string;
  groupId?: string;
  group_id?: string;
  name: string;
  displayName?: string;
  dataType: string;
  constraints?: {
    fields?: BackendSchemaField[];
    [key: string]: unknown;
  };
  metadata?: {
    description?: string;
    tags?: string[];
    [key: string]: unknown;
  };
  visibility?: string;
  status?: string;
  currentVersion?: number;
  latestPublishedVersion?: number;
  deprecatedAt?: string;
  deprecatedReason?: string;
  replacedBy?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStringField(value: unknown, key: string): string {
  if (!isRecord(value)) {
    return '';
  }

  const fieldValue = Reflect.get(value, key);
  return typeof fieldValue === 'string' ? fieldValue : '';
}

function isBackendSchema(value: unknown): value is BackendSchema {
  if (!isRecord(value)) {
    return false;
  }

  const maybeName = Reflect.get(value, 'name');
  const maybeDataType = Reflect.get(value, 'dataType');

  return typeof maybeName === 'string' && (maybeDataType === undefined || typeof maybeDataType === 'string');
}

// ============================================================================
// Transform Functions
// ============================================================================

/**
 * Transform frontend Schema to backend format
 */
const transformToBackend = (input: CreateSchemaInput): Record<string, unknown> => {
  return {
    name: input.name,
    displayName: input.displayName,
    dataType: 'object', // Multi-field schemas are always 'object' type
    constraints: {
      fields: input.fields.map((field) => ({
        id: field.id,
        name: field.name,
        type: field.type.toUpperCase(), // Backend expects uppercase
        required: field.required,
        description: field.description,
        constraints: field.constraints,
        ...(field.storage ? { storage: field.storage } : {}),
      })),
    },
    metadata: {
      description: input.description || `Schema definition for ${input.name}`,
      tags: input.tags || [],
    },
    visibility: input.visibility || 'private',
  };
};

/**
 * Transform backend Schema to frontend format
 */
const transformFromBackend = (backend: BackendSchema): Schema => {
  const fields: SchemaField[] = (backend.constraints?.fields || []).map((f: BackendSchemaField) => ({
    id: f.id || `field_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    name: f.name,
    type: normalizeSchemaFieldType(f.type),
    required: f.required ?? false,
    description: f.description,
    constraints: f.constraints,
    storage: f.storage,
  }));

  return {
    id: backend._id || backend.id || '',
    organizationId: backend.organizationId || backend.org_id || '',
    ...(typeof backend.groupId === 'string' && backend.groupId
      ? { groupId: backend.groupId }
      : typeof backend.group_id === 'string' && backend.group_id
        ? { groupId: backend.group_id }
        : {}),
    name: backend.name,
    displayName: backend.displayName || backend.name,
    description: backend.metadata?.description || '',
    fields,
    currentVersion: backend.currentVersion || 1,
    latestPublishedVersion: backend.latestPublishedVersion || 0,
    visibility: normalizeSchemaVisibility(backend.visibility),
    status: normalizeSchemaStatus(backend.status),
    deprecatedAt: backend.deprecatedAt,
    deprecatedReason: backend.deprecatedReason,
    replacedBy: backend.replacedBy,
    createdBy: backend.createdBy || '',
    updatedBy: backend.updatedBy || '',
    createdAt: backend.createdAt || new Date().toISOString(),
    updatedAt: backend.updatedAt || new Date().toISOString(),
  };
};

// ============================================================================
// Constants
// ============================================================================

export const FIELD_TYPES: { value: SchemaFieldType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

export const SCHEMA_STATUSES: { value: SchemaStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  { value: 'published', label: 'Published', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'deprecated', label: 'Deprecated', color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400' },
];

export const STRING_FORMATS: { value: StringFormat; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'uuid', label: 'UUID' },
  { value: 'datetime', label: 'Date/Time' },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a new field with default values
 */
export const createDefaultField = (): SchemaField => ({
  id: `field_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
  name: '',
  type: 'string',
  required: false,
});

/**
 * Generate a snake_case name from display name
 */
export const generateSchemaName = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^[0-9]/, '_$&'); // Prefix with _ if starts with number
};

/**
 * Validate schema name format
 */
export const isValidSchemaName = (name: string): boolean => {
  return /^[a-z][a-z0-9_]*$/.test(name);
};

// ============================================================================
// JSON-to-Schema Flattening
// ============================================================================

export type InferredLeafType = 'string' | 'number' | 'boolean' | 'date';

/**
 * A single primitive-leaf field extracted from a parsed JSON sample.
 *
 * `pathParts` is the raw key path in the source JSON, with array steps
 * encoded as `<arrayKey>[]` so callers can group/display by source.
 * `groupLabel` is a human-friendly label for the source group:
 *   - "" (empty) for top-level fields
 *   - "services[]" for items of a top-level array of objects
 *   - "obj" for fields under a top-level nested object
 */
export interface InferredLeaf {
  name: string;
  type: InferredLeafType;
  sampleValue: unknown;
  pathParts: string[];
  groupLabel: string;
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;

const inferLeafType = (value: unknown): InferredLeafType => {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string' && ISO_DATE_PREFIX.test(value) && !isNaN(Date.parse(value))) {
    return 'date';
  }
  return 'string';
};

const sanitizeNamePart = (key: string): string =>
  key
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

const ensureLeadingLetter = (name: string): string =>
  /^[a-z]/.test(name) ? name : `field_${name}`;

const makeUniqueName = (base: string, taken: Set<string>): string => {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
};

/**
 * Generate the schema field name from a raw path. Within a group (arrays of
 * objects), only the final key is used so e.g. `services[].service` produces
 * `service`, not `services_service`. For nested non-array paths, the parts
 * are joined with `_` to preserve context.
 */
const generateNameFromPath = (pathParts: string[]): string => {
  // Find the last array marker — fields after it are scoped to that array's items
  let lastArrayIdx = -1;
  for (let i = pathParts.length - 1; i >= 0; i -= 1) {
    if (pathParts[i].endsWith('[]')) {
      lastArrayIdx = i;
      break;
    }
  }
  const nameParts = lastArrayIdx >= 0 ? pathParts.slice(lastArrayIdx + 1) : pathParts;
  const cleaned = nameParts.map(sanitizeNamePart).filter((p) => p.length > 0).join('_');
  if (cleaned.length === 0) {
    // Fallback: name the array itself (e.g. an array of primitives or empty array)
    const fallback = pathParts.map((p) => sanitizeNamePart(p.replace(/\[\]$/, ''))).filter((p) => p.length > 0).join('_');
    return fallback.length === 0 ? '' : ensureLeadingLetter(fallback);
  }
  return ensureLeadingLetter(cleaned);
};

/**
 * Group label = the path up to and including the deepest array marker, with
 * `[]` retained for clarity (e.g. "services[]"). Top-level fields get "".
 */
const groupLabelFromPath = (pathParts: string[]): string => {
  let lastArrayIdx = -1;
  for (let i = pathParts.length - 1; i >= 0; i -= 1) {
    if (pathParts[i].endsWith('[]')) {
      lastArrayIdx = i;
      break;
    }
  }
  if (lastArrayIdx >= 0) {
    return pathParts.slice(0, lastArrayIdx + 1).join('.');
  }
  if (pathParts.length > 1) {
    return pathParts.slice(0, -1).join('.');
  }
  return '';
};

interface CollectState {
  leaves: InferredLeaf[];
  takenNames: Set<string>;
  pathSeen: Set<string>;
}

const emitLeaf = (
  pathParts: string[],
  type: InferredLeafType,
  sampleValue: unknown,
  state: CollectState,
): void => {
  const pathKey = pathParts.join('|');
  if (state.pathSeen.has(pathKey)) return;
  state.pathSeen.add(pathKey);

  const baseName = generateNameFromPath(pathParts);
  if (!baseName) return;
  const name = makeUniqueName(baseName, state.takenNames);
  state.takenNames.add(name);
  state.leaves.push({
    name,
    type,
    sampleValue,
    pathParts,
    groupLabel: groupLabelFromPath(pathParts),
  });
};

const collectLeaves = (
  value: unknown,
  pathParts: string[],
  state: CollectState,
): void => {
  if (value === null || value === undefined) {
    if (pathParts.length === 0) return;
    emitLeaf(pathParts, 'string', value, state);
    return;
  }

  if (typeof value !== 'object') {
    emitLeaf(pathParts, inferLeafType(value), value, state);
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      if (pathParts.length > 0) emitLeaf(pathParts, 'string', value, state);
      return;
    }
    const first = value[0];
    const firstIsObject =
      first !== null && typeof first === 'object' && !Array.isArray(first);
    if (firstIsObject) {
      // Append `[]` marker to the last path part to denote we descended into items
      const arrayPath = [...pathParts.slice(0, -1), `${pathParts[pathParts.length - 1]}[]`];
      for (const item of value) {
        if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
          for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
            collectLeaves(v, [...arrayPath, k], state);
          }
        }
      }
      return;
    }
    emitLeaf(pathParts, inferLeafType(first), value, state);
    return;
  }

  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    collectLeaves(v, [...pathParts, k], state);
  }
};

/**
 * Flatten a parsed JSON object into a list of primitive-leaf fields with
 * sanitized snake_case names and source-path metadata.
 *
 * Naming rules:
 * - Top-level scalars: bare key (`timestamp`)
 * - Nested non-array objects: `_`-joined path (`outer_inner`)
 * - Array-of-object items: bare child key (`service`, not `services_service`)
 *   so the schema can naturally model one item of the array
 *
 * Each leaf includes `pathParts` (with `[]` markers for array steps) and a
 * `groupLabel` callers can use to render grouped sections in the UI.
 */
export const flattenJsonToLeaves = (root: Record<string, unknown>): InferredLeaf[] => {
  const state: CollectState = {
    leaves: [],
    takenNames: new Set(),
    pathSeen: new Set(),
  };
  for (const [k, v] of Object.entries(root)) {
    collectLeaves(v, [k], state);
  }
  return state.leaves;
};

// ============================================================================
// API Functions
// ============================================================================

export const schemaApi = {
  /**
   * List schemas for the current organization
   */
  listSchemas: async (
    options?: ListSchemasOptions,
    axiosConfig?: import('axios').AxiosRequestConfig
  ): Promise<PaginatedResult<Schema>> => {
    const params: Record<string, string | number> = {};
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    if (options?.search) params.search = options.search;
    if (options?.status) params.status = options.status;
    if (options?.visibility) params.visibility = options.visibility;
    params.scope = options?.scope ?? 'current';

    const response = await dataServiceClient.get('/api/schemas', {
      params,
      ...(axiosConfig?.headers ? { headers: axiosConfig.headers } : {}),
    });
    const rawData = response.data.data || response.data.schemas || [];
    const data = Array.isArray(rawData) ? rawData.map(transformFromBackend) : [];

    return {
      data,
      pagination: response.data.pagination || {
        page: 1,
        limit: 20,
        total: data.length,
        pages: 1,
      },
    };
  },

  /**
   * Get a schema by ID
   */
  getSchema: async (
    id: string,
    axiosConfig?: import('axios').AxiosRequestConfig
  ): Promise<Schema> => {
    const response = await dataServiceClient.get(`/api/schemas/${id}`, axiosConfig);
    const rawData = response.data.data || response.data;
    return transformFromBackend(rawData);
  },

  /**
   * Get groups that contain a schema
   */
  getSchemaGroups: async (
    id: string,
    axiosConfig?: import('axios').AxiosRequestConfig,
  ): Promise<SchemaGroupMembershipSummary[]> => {
    const response = await dataServiceClient.get(`/api/schemas/${id}/groups`, axiosConfig);
    const responseData = response.data;
    const rawData = responseData.data;
    const rawMemberships = Array.isArray(rawData)
      ? rawData
      : rawData?.memberships || responseData.memberships || [];

    if (!Array.isArray(rawMemberships)) {
      return [];
    }

    const memberships = rawMemberships
      .map((membership): SchemaGroupMembershipSummary | null => {
        const groupId = getStringField(membership, 'groupId');
        if (!groupId) {
          return null;
        }

        return {
          groupId,
          groupName: getStringField(membership, 'groupName'),
          groupSlug: getStringField(membership, 'groupSlug'),
          collectionName: getStringField(membership, 'collectionName'),
          status: getStringField(membership, 'status'),
        };
      })
      .filter((membership): membership is SchemaGroupMembershipSummary => membership !== null);

    return memberships;
  },

  /**
   * Get schema versions
   */
  getSchemaVersions: async (id: string): Promise<unknown[]> => {
    const response = await dataServiceClient.get(`/api/schemas/${id}/versions`);
    return response.data.data || response.data.versions || [];
  },

  /**
   * Create a new schema
   */
  createSchema: async (
    input: CreateSchemaInput,
    options?: DataServiceScopeOptions,
  ): Promise<Schema> => {
    const backendInput = transformToBackend(input);
    const response = await dataServiceClient.post(
      '/api/schemas',
      backendInput,
      getScopedRequestConfig(options?.organizationId),
    );
    const rawData = response.data.data || response.data;
    return transformFromBackend(rawData);
  },

  /**
   * Update a schema
   */
  updateSchema: async (id: string, input: UpdateSchemaInput): Promise<Schema> => {
    const backendInput: Record<string, unknown> = {};

    if (input.displayName !== undefined) {
      backendInput.displayName = input.displayName;
    }

    if (input.fields !== undefined) {
      backendInput.constraints = {
        fields: input.fields.map((field) => ({
          id: field.id,
          name: field.name,
          type: field.type.toUpperCase(),
          required: field.required,
          description: field.description,
          constraints: field.constraints,
          ...(field.storage ? { storage: field.storage } : {}),
        })),
      };
    }

    if (input.description !== undefined || input.tags !== undefined) {
      const metadata: Record<string, unknown> = {};
      if (input.description !== undefined) {
        metadata.description = input.description;
      }
      if (input.tags !== undefined) {
        metadata.tags = input.tags;
      }
      backendInput.metadata = metadata;
    }

    if (input.visibility !== undefined) {
      backendInput.visibility = input.visibility;
    }

    const response = await dataServiceClient.put(`/api/schemas/${id}`, backendInput);
    const rawData = response.data.data || response.data;
    return transformFromBackend(rawData);
  },

  /**
   * Delete a schema (only drafts)
   */
  deleteSchema: async (id: string): Promise<void> => {
    await dataServiceClient.delete(`/api/schemas/${id}`);
  },

  /**
   * Publish a schema
   */
  publishSchema: async (
    id: string,
    options?: DataServiceScopeOptions,
  ): Promise<Schema> => {
    const response = await dataServiceClient.post(
      `/api/schemas/${id}/publish`,
      undefined,
      getScopedRequestConfig(options?.organizationId),
    );
    const responseData = response.data.data || response.data;
    const rawData =
      responseData && typeof responseData === 'object' && 'schema' in responseData
        ? responseData.schema
        : responseData;
    if (!isBackendSchema(rawData)) {
      throw new Error('Invalid schema publish response.');
    }
    return transformFromBackend(rawData);
  },

  /**
   * Deprecate a schema
   */
  deprecateSchema: async (
    id: string,
    reason: string,
    replacedBy?: string
  ): Promise<Schema> => {
    const response = await dataServiceClient.post(`/api/schemas/${id}/deprecate`, {
      reason,
      replacedBy,
    });
    const rawData = response.data.data || response.data;
    return transformFromBackend(rawData);
  },

  /**
   * Fork a schema (create a copy in your organization)
   */
  forkSchema: async (id: string, name?: string): Promise<Schema> => {
    const response = await dataServiceClient.post(`/api/schemas/${id}/fork`, { name });
    const rawData = response.data.data || response.data;
    return transformFromBackend(rawData);
  },

  /**
   * Validate a value against a schema
   */
  validateValue: async (
    schemaId: string,
    value: unknown
  ): Promise<{ valid: boolean; errors?: string[] }> => {
    const response = await dataServiceClient.post('/api/schemas/validate', {
      schemaId,
      value,
    });
    return response.data.data || response.data;
  },

  /**
   * Search accessible schemas (own + shared + public)
   */
  searchSchemas: async (options?: {
    search?: string;
    includeOwn?: boolean;
    includeShared?: boolean;
    includePublic?: boolean;
    status?: SchemaStatus;
    scope?: DataServiceListingScope;
  }): Promise<Schema[]> => {
    const params: Record<string, string | boolean> = {};
    if (options?.search) params.q = options.search;
    if (options?.includeOwn !== undefined) params.includeOwn = options.includeOwn;
    if (options?.includeShared !== undefined) params.includeShared = options.includeShared;
    if (options?.includePublic !== undefined) params.includePublic = options.includePublic;
    if (options?.status) params.status = options.status;
    params.scope = options?.scope ?? 'current';

    const response = await dataServiceClient.get('/api/schemas/search', { params });
    const rawData = response.data.data || response.data.schemas || [];
    return Array.isArray(rawData) ? rawData.map(transformFromBackend) : [];
  },
};

export default schemaApi;
